import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Target, Plus, ArrowRight, Clock, CheckCircle, XCircle, Loader, Brain, Shield,
  AlertTriangle, Server, Search, Filter, LayoutGrid, List, RefreshCw, ChevronDown, ChevronUp,
  Calendar, Activity, TrendingUp, Eye, Trash2, MoreVertical, Download, FileText,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Globe, CheckSquare, BadgeCheck,
  Factory, Wifi, Building2, Pause, GripVertical, Link2, Unlink, FolderOpen, ArrowUpDown, Tag,
  Gauge, Lock, Crown, RotateCcw, Cloud
} from 'lucide-react';
import { scans as scansApi, otScan as otScanApi, iotScan as iotScanApi, workspaces as workspacesApi } from '../../api/client';
import AIScanWizard from '../../components/wizards/AIScanWizard';
import ExpertScanWizard from '../../components/wizards/ExpertScanWizard';
import APIScanWizard from '../../components/wizards/APIScanWizard';
import OTScanWizard from '../../components/wizards/OTScanWizard';
import IoTScanWizard from '../../components/wizards/IoTScanWizard';
import { ScansWelcomeBanner } from '../../components/WelcomeBanner';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import NoWorkspaceModal from '../../components/NoWorkspaceModal';
import SankeyWorkspaceFlow from '../../components/SankeyWorkspaceFlow';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import { useLicense } from '../../hooks/useLicense';
import LicenseBlock from '../../components/LicenseBlock';
import { useI18n } from '../../i18n';
import { useWorkspace } from '../../contexts/WorkspaceContext';

// Cache utilities
const CACHE_KEYS = {
  SCANS_LIST: 'scans_list_cache',
  WORKSPACES_LIST: 'workspaces_list_cache',
};

const CACHE_TTL = {
  SCANS: 30 * 1000,      // 30 seconds
  WORKSPACES: 2 * 60 * 1000, // 2 minutes
};

const getCache = (key, workspaceId = null) => {
  try {
    const cacheKey = workspaceId ? `${key}_${workspaceId}` : key;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    const { data, timestamp, ttl } = JSON.parse(cached);
    if (Date.now() - timestamp > ttl) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
};

const setCache = (key, data, ttl, workspaceId = null) => {
  try {
    const cacheKey = workspaceId ? `${key}_${workspaceId}` : key;
    localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now(), ttl }));
  } catch (e) {
    // Storage full or unavailable
  }
};

const ITEMS_PER_PAGE = 20;

const ScansList = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { workspaceId: activeWorkspaceId, activeWorkspace } = useWorkspace();
  const { isFreePlan, isSimulationMode, hasFeature, getQuotaStatus, currentPlan } = usePlanRestrictions();
  const { isBlocked: licenseBlocked } = useLicense();

  // Initialize from cache for instant display
  const cachedScans = getCache(CACHE_KEYS.SCANS_LIST, activeWorkspaceId);
  const cachedWorkspaces = getCache(CACHE_KEYS.WORKSPACES_LIST);

  const [scansList, setScansList] = useState(cachedScans || []);
  const [loading, setLoading] = useState(!cachedScans);
  const [fetchError, setFetchError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [sortBy, setSortBy] = useState('date'); // 'name', 'target', 'date', 'status', 'vulns', 'type'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [isAIWizardOpen, setIsAIWizardOpen] = useState(false);
  const [isExpertWizardOpen, setIsExpertWizardOpen] = useState(false);
  const [isAPIWizardOpen, setIsAPIWizardOpen] = useState(false);
  const [isOTWizardOpen, setIsOTWizardOpen] = useState(false);
  const [isIoTWizardOpen, setIsIoTWizardOpen] = useState(false);
  const [selectedScans, setSelectedScans] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isValidating, setIsValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);
  const [isRescanning, setIsRescanning] = useState(false);
  const [workspacesList, setWorkspacesList] = useState(cachedWorkspaces || []);
  const [draggedScanId, setDraggedScanId] = useState(null);
  const [dropTargetWorkspace, setDropTargetWorkspace] = useState(null);
  const [isAssociating, setIsAssociating] = useState(false);
  const [associationMessage, setAssociationMessage] = useState(null);
  const [showNoWorkspaceModal, setShowNoWorkspaceModal] = useState(false);

  // Function to check if workspaces exist before opening scan wizard
  const openScanWizard = (wizardSetter) => {
    if (workspacesList.length === 0) {
      setShowNoWorkspaceModal(true);
    } else {
      wizardSetter(true);
    }
  };

  // Helper function to translate scan types
  const translateScanType = (scanType) => {
    if (!scanType) return '-';
    const translationKey = `osint.scanTypes.${scanType}`;
    const translated = t(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }
    // Fallback: format the scan type nicely
    return scanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  useEffect(() => {
    // Reset immédiatement au changement de workspace pour éviter d'afficher
    // les scans de l'ancien workspace pendant le chargement du nouveau
    const cached = getCache(CACHE_KEYS.SCANS_LIST, activeWorkspaceId);
    setScansList(cached || []);
    setLoading(!cached);
    fetchScans(true); // Initial load with retry logic
    fetchWorkspaces();
    const interval = setInterval(() => fetchScans(false), 10000); // Background refresh without retry
    return () => clearInterval(interval);
  }, [activeWorkspaceId]);

  const fetchWorkspaces = async () => {
    try {
      const data = await workspacesApi.list(0, 100);
      // API returns array directly, not { workspaces: [...] }
      const workspaces = Array.isArray(data) ? data : (data.workspaces || []);
      setWorkspacesList(workspaces);
      setCache(CACHE_KEYS.WORKSPACES_LIST, workspaces, CACHE_TTL.WORKSPACES);
    } catch (error) {
      // Handle 401 - user will be redirected by API client, don't log error
      if (error.response?.status === 401) {
        return;
      }
      console.error('Failed to fetch workspaces:', error);
    }
  };

  const fetchScans = async (isInitialLoad = false, retryCount = 0) => {
    const MAX_RETRIES = 2;
    try {
      setFetchError(null);
      if (isInitialLoad) setLoading(true);
      // Use active workspace from context
      const data = await scansApi.list(0, 100, activeWorkspaceId);
      const scans = data.scans || [];
      setScansList(scans);
      setCache(CACHE_KEYS.SCANS_LIST, scans, CACHE_TTL.SCANS, activeWorkspaceId);
    } catch (error) {
      console.error('Failed to fetch scans:', error);

      // Handle 401 - user will be redirected by API client, don't show error
      if (error.response?.status === 401) {
        return;
      }

      // Retry on initial load before showing error
      if (isInitialLoad && retryCount < MAX_RETRIES) {
        const delay = 1000 * (retryCount + 1); // 1s, 2s
        console.warn(`[ScansList] Retrying initial load (${retryCount + 1}/${MAX_RETRIES}) in ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchScans(true, retryCount + 1);
      }

      // Only set error if we don't have cached data AND this isn't a background refresh
      // For background refreshes (not initial load), silently fail and keep cached data
      if (isInitialLoad && (!scansList || scansList.length === 0)) {
        setFetchError(error.response?.data?.detail || 'Erreur lors du chargement des scans');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchScans(true); // Use retry logic for manual refresh
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: <CheckCircle className="w-5 h-5 text-brand-green" />,
      running: <Loader className="w-5 h-5 text-blue-400 animate-spin" />,
      waiting: <Clock className="w-5 h-5 text-orange-400 animate-pulse" />,
      pending: <Clock className="w-5 h-5 text-yellow-400" />,
      failed: <XCircle className="w-5 h-5 text-red-400" />,
      stopped: <Pause className="w-5 h-5 text-gray-400" />,
    };
    return icons[status] || <Clock className="w-5 h-5 text-gray-400" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-brand-green/20 text-brand-green border-brand-green/50',
      running: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      waiting: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      failed: 'bg-red-500/20 text-red-400 border-red-500/50',
      stopped: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/50';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-purple-500',
      high: 'bg-red-500',
      medium: 'bg-orange-500',
      low: 'bg-yellow-500',
      info: 'bg-blue-500',
    };
    return colors[severity] || 'bg-gray-500';
  };

  // Calculate CyberScore based on vulnerabilities
  const getCyberScore = (scan) => {
    if (scan.status === 'running' || scan.status === 'pending' || scan.status === 'waiting') {
      return { score: null, label: 'En cours', color: 'text-blue-400', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500/50' };
    }

    const critical = scan.critical_count || 0;
    const high = scan.high_count || 0;
    const medium = scan.medium_count || 0;
    const low = scan.low_count || 0;

    // Weighted score calculation
    // Critical = -40 points each, High = -20, Medium = -10, Low = -5
    let score = 100;
    score -= critical * 40;
    score -= high * 20;
    score -= medium * 10;
    score -= low * 5;
    score = Math.max(0, Math.min(100, score));

    let label, color, bgColor, borderColor;
    if (score >= 90) {
      label = 'Excellent';
      color = 'text-green-400';
      bgColor = 'bg-green-500/20';
      borderColor = 'border-green-500/50';
    } else if (score >= 70) {
      label = 'Bon';
      color = 'text-brand-green';
      bgColor = 'bg-brand-green/20';
      borderColor = 'border-brand-green/50';
    } else if (score >= 50) {
      label = 'Moyen';
      color = 'text-yellow-400';
      bgColor = 'bg-yellow-500/20';
      borderColor = 'border-yellow-500/50';
    } else if (score >= 30) {
      label = 'Faible';
      color = 'text-orange-400';
      bgColor = 'bg-orange-500/20';
      borderColor = 'border-orange-500/50';
    } else {
      label = 'Critique';
      color = 'text-red-400';
      bgColor = 'bg-red-500/20';
      borderColor = 'border-red-500/50';
    }

    return { score, label, color, bgColor, borderColor };
  };

  // Stats calculations
  const stats = {
    total: scansList.length,
    running: scansList.filter(s => s.status === 'running').length,
    completed: scansList.filter(s => s.status === 'completed').length,
    failed: scansList.filter(s => s.status === 'failed').length,
    waiting: scansList.filter(s => s.status === 'waiting' || s.status === 'pending').length,
    totalVulns: scansList.reduce((sum, s) => sum + (s.total_vulnerabilities || 0), 0),
    criticalVulns: scansList.reduce((sum, s) => sum + (s.critical_count || 0), 0),
    highVulns: scansList.reduce((sum, s) => sum + (s.high_count || 0), 0),
    totalHosts: scansList.reduce((sum, s) => sum + (s.total_hosts_found || s.hosts_up || 0), 0),
  };

  // Filter and search
  let filteredScans = filter === 'all'
    ? scansList
    : scansList.filter(s => s.status === filter);

  if (searchQuery) {
    filteredScans = filteredScans.filter(s =>
      s.target?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.scan_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Sort function for column click
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc for date, asc for text
      setSortBy(column);
      setSortDirection(column === 'date' ? 'desc' : 'asc');
    }
  };

  // Sort
  filteredScans = [...filteredScans].sort((a, b) => {
    let comparison = 0;

    if (sortBy === 'date') {
      comparison = new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === 'status') {
      const order = { running: 0, pending: 1, completed: 2, failed: 3 };
      comparison = (order[a.status] || 4) - (order[b.status] || 4);
    } else if (sortBy === 'vulns') {
      comparison = (b.total_vulnerabilities || 0) - (a.total_vulnerabilities || 0);
    } else if (sortBy === 'name') {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      comparison = nameA.localeCompare(nameB);
    } else if (sortBy === 'target') {
      const targetA = (a.target || '').toLowerCase();
      const targetB = (b.target || '').toLowerCase();
      comparison = targetA.localeCompare(targetB);
    } else if (sortBy === 'type') {
      const typeA = (a.scan_type || '').toLowerCase();
      const typeB = (b.scan_type || '').toLowerCase();
      comparison = typeA.localeCompare(typeB);
    } else if (sortBy === 'score') {
      const scoreA = getCyberScore(a).score ?? -1;
      const scoreB = getCyberScore(b).score ?? -1;
      comparison = scoreB - scoreA;
    }

    // Reverse if descending (for non-date fields), for date it's already desc by default
    if (sortDirection === 'asc' && sortBy !== 'date') {
      return comparison;
    } else if (sortDirection === 'desc' && sortBy !== 'date') {
      return -comparison;
    } else if (sortDirection === 'asc' && sortBy === 'date') {
      return -comparison;
    }
    return comparison;
  });

  // Pagination
  const totalPages = Math.ceil(filteredScans.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedScans = filteredScans.slice(startIndex, endIndex);

  // Reset page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, sortBy]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleSelectScan = (scanId) => {
    setSelectedScans(prev =>
      prev.includes(scanId)
        ? prev.filter(id => id !== scanId)
        : [...prev, scanId]
    );
  };

  const handleValidateForReport = async () => {
    if (selectedScans.length === 0) return;

    setIsValidating(true);
    try {
      const result = await scansApi.validateForReport(selectedScans, true);
      setValidationMessage({
        type: 'success',
        text: `${result.validated_count} scan(s) validé(s) pour les rapports`
      });
      setSelectedScans([]);
      await fetchScans();
      setTimeout(() => setValidationMessage(null), 3000);
    } catch (error) {
      console.error('Failed to validate scans:', error);
      setValidationMessage({
        type: 'error',
        text: 'Erreur lors de la validation des scans'
      });
      setTimeout(() => setValidationMessage(null), 3000);
    } finally {
      setIsValidating(false);
    }
  };

  const handleStopScan = async (e, scanId) => {
    e.stopPropagation();
    if (window.confirm('Êtes-vous sûr de vouloir arrêter ce scan ?')) {
      try {
        await scansApi.stop(scanId);
        fetchScans();
      } catch (error) {
        console.error('Failed to stop scan:', error);
        alert('Échec de l\'arrêt du scan');
      }
    }
  };

  const handleResumeScan = async (e, scanId) => {
    e.stopPropagation();
    try {
      await scansApi.resume(scanId);
      setAssociationMessage({
        type: 'success',
        text: 'Scan repris avec succès'
      });
      fetchScans();
      setTimeout(() => setAssociationMessage(null), 3000);
    } catch (error) {
      console.error('Failed to resume scan:', error);
      setAssociationMessage({
        type: 'error',
        text: 'Échec de la reprise du scan'
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    }
  };

  // State for export
  const [isExporting, setIsExporting] = useState(false);
  const [regeneratingScanId, setRegeneratingScanId] = useState(null);

  // Resilient fetch wrapper with retry for network errors (ERR_NETWORK_CHANGED)
  const resilientFetch = async (url, options, maxRetries = 3) => {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);
        return response;
      } catch (error) {
        lastError = error;
        const isNetworkError = !error.response && (
          error.message?.includes('Network Error') ||
          error.message?.includes('ERR_NETWORK') ||
          error.message?.includes('ERR_NETWORK_CHANGED') ||
          error.message?.includes('Failed to fetch') ||
          error.name === 'TypeError'
        );

        if (isNetworkError && attempt < maxRetries - 1) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = 1000 * Math.pow(2, attempt);
          console.warn(`[ScansList] Network error, retrying (${attempt + 1}/${maxRetries}) in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));

          // Wait for online status if offline
          if (!navigator.onLine) {
            await new Promise((resolve) => {
              const checkOnline = setInterval(() => {
                if (navigator.onLine) {
                  clearInterval(checkOnline);
                  // Extra stabilization delay after network change
                  setTimeout(resolve, 2000);
                }
              }, 500);
              // Timeout after 15 seconds
              setTimeout(() => {
                clearInterval(checkOnline);
                resolve();
              }, 15000);
            });
          }
        } else {
          throw error;
        }
      }
    }
    throw lastError;
  };

  // Regenerate reports for a scan
  const handleRegenerateReports = async (e, scanId) => {
    e.stopPropagation();
    setRegeneratingScanId(scanId);

    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');

      // Regenerate PDF report (with network retry)
      const pdfResponse = await resilientFetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scan_ids: [scanId],
          report_type: 'technical',
          template: 'professional',
          format: 'pdf',
          language: 'fr'
        })
      });

      // Handle 401 - redirect to login
      if (pdfResponse.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      if (!pdfResponse.ok) {
        throw new Error('Échec de la régénération PDF');
      }

      // Regenerate DOCX report (with network retry)
      const docxResponse = await resilientFetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scan_ids: [scanId],
          report_type: 'technical',
          template: 'professional',
          format: 'docx',
          language: 'fr'
        })
      });

      // Handle 401 - redirect to login
      if (docxResponse.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      if (!docxResponse.ok) {
        throw new Error('Échec de la régénération Word');
      }

      setAssociationMessage({
        type: 'success',
        text: 'Rapports PDF et Word régénérés avec succès'
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    } catch (error) {
      console.error('Regeneration failed:', error);
      setAssociationMessage({
        type: 'error',
        text: `Échec de la régénération: ${error.message}`
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    } finally {
      setRegeneratingScanId(null);
    }
  };

  const handleExport = async (e, scanId, format) => {
    e.stopPropagation();
    setIsExporting(true);

    try {
      // Use resilientFetch for network error recovery (ERR_NETWORK_CHANGED)
      const response = await resilientFetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/generated-reports/scan/${scanId}/export-passi?format=${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}`
        }
      });

      // Handle 401 - redirect to login
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_pentest_${scanId}.${format === 'pdf' ? 'pdf' : 'docx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setAssociationMessage({
        type: 'success',
        text: `Rapport ${format.toUpperCase()} téléchargé avec succès`
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    } catch (error) {
      console.error('Export failed:', error);
      const isNetworkError = error.message?.includes('ERR_NETWORK') ||
                             error.message?.includes('Failed to fetch') ||
                             error.message?.includes('Network');
      setAssociationMessage({
        type: 'error',
        text: isNetworkError
          ? 'Erreur réseau. Vérifiez votre connexion et réessayez.'
          : `Échec de l'export ${format.toUpperCase()}`
      });
      setTimeout(() => setAssociationMessage(null), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  // Drag & Drop handlers
  const handleDragStart = useCallback((e, scanId) => {
    e.stopPropagation();
    // If the dragged scan is part of selected scans, use all selected scans
    // Otherwise, just use the single dragged scan
    const scansToDrag = selectedScans.includes(scanId) && selectedScans.length > 1
      ? selectedScans
      : [scanId];
    setDraggedScanId(scanId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(scansToDrag));
  }, [selectedScans]);

  const handleDragEnd = useCallback(() => {
    setDraggedScanId(null);
    setDropTargetWorkspace(null);
  }, []);

  const handleDragOver = useCallback((e, workspaceId) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetWorkspace(workspaceId);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDropTargetWorkspace(null);
  }, []);

  const handleDrop = useCallback(async (e, workspaceId) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetWorkspace(null);

    if (!draggedScanId) return;

    // Parse scan IDs from dataTransfer (can be single or multiple)
    let scansToAssociate = [draggedScanId];
    try {
      const transferData = e.dataTransfer.getData('text/plain');
      const parsedData = JSON.parse(transferData);
      if (Array.isArray(parsedData) && parsedData.length > 0) {
        scansToAssociate = parsedData;
      }
    } catch {
      // If parsing fails, use the single draggedScanId
    }

    setIsAssociating(true);
    try {
      await workspacesApi.associateScans(workspaceId, scansToAssociate);
      const workspace = workspacesList.find(w => w.id === workspaceId);
      const scanCount = scansToAssociate.length;
      setAssociationMessage({
        type: 'success',
        text: scanCount > 1
          ? `${scanCount} scans associés à ${workspace?.company_name || workspace?.name || 'Workspace'}`
          : `Scan associé à ${workspace?.company_name || workspace?.name || 'Workspace'}`
      });
      setSelectedScans([]); // Clear selection after successful drop
      await fetchScans();
      setTimeout(() => setAssociationMessage(null), 3000);
    } catch (error) {
      console.error('Failed to associate scan(s):', error);
      setAssociationMessage({
        type: 'error',
        text: 'Erreur lors de l\'association des scans'
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    } finally {
      setIsAssociating(false);
      setDraggedScanId(null);
    }
  }, [draggedScanId, workspacesList, fetchScans]);

  const handleRemoveScanFromWorkspace = async (e, scanId, workspaceId) => {
    e.stopPropagation();
    try {
      await workspacesApi.removeScans(workspaceId, [scanId]);
      setAssociationMessage({
        type: 'success',
        text: 'Scan retiré du workspace'
      });
      await fetchScans();
      setTimeout(() => setAssociationMessage(null), 3000);
    } catch (error) {
      console.error('Failed to remove scan from workspace:', error);
      setAssociationMessage({
        type: 'error',
        text: 'Erreur lors du retrait du scan'
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedScans.length === 0) return;

    const confirmMessage = selectedScans.length === 1
      ? 'Êtes-vous sûr de vouloir supprimer ce scan ?'
      : `Êtes-vous sûr de vouloir supprimer ces ${selectedScans.length} scans ?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      // Delete all selected scans
      await Promise.all(selectedScans.map(scanId => scansApi.delete(scanId)));
      setAssociationMessage({
        type: 'success',
        text: `${selectedScans.length} scan(s) supprimé(s)`
      });
      setSelectedScans([]);
      await fetchScans();
      setTimeout(() => setAssociationMessage(null), 3000);
    } catch (error) {
      console.error('Failed to delete scans:', error);
      setAssociationMessage({
        type: 'error',
        text: 'Erreur lors de la suppression des scans'
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    }
  };

  // Rescan handler - relaunch selected scans with their original parameters
  const handleRescan = async () => {
    if (selectedScans.length === 0) return;

    const confirmMessage = selectedScans.length === 1
      ? 'Relancer ce scan avec ses paramètres d\'origine ?'
      : `Relancer ces ${selectedScans.length} scans avec leurs paramètres d\'origine ?`;

    if (!window.confirm(confirmMessage)) return;

    setIsRescanning(true);
    try {
      const result = await scansApi.rescan(selectedScans);
      if (result.success) {
        setAssociationMessage({
          type: 'success',
          text: `${result.created_scans.length} scan(s) relancé(s)`
        });
        setSelectedScans([]);
        await fetchScans();
      } else {
        setAssociationMessage({
          type: 'error',
          text: result.message || 'Erreur lors du relancement'
        });
      }
      setTimeout(() => setAssociationMessage(null), 3000);
    } catch (error) {
      console.error('Failed to rescan:', error);
      setAssociationMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Erreur lors du relancement des scans'
      });
      setTimeout(() => setAssociationMessage(null), 3000);
    } finally {
      setIsRescanning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] bg-brand-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show error state if fetch failed and no cached data
  if (fetchError && scansList.length === 0) {
    return (
      <div className="min-h-[50vh] bg-brand-black flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Erreur de chargement</h2>
          <p className="text-gray-400 mb-6">{fetchError}</p>
          <button
            onClick={handleRefresh}
            className="bg-brand-green text-brand-black px-6 py-3 rounded-lg font-bold hover:bg-green-600 transition-all inline-flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Réessayer</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-black p-6 overflow-x-hidden">
      {/* Main Content - Centered */}
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6 overflow-x-hidden">
        {/* Welcome Banner */}
        <ScansWelcomeBanner />

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gradient-green flex items-center gap-3">
              <Target className="w-10 h-10 text-brand-green" />
              {t('scans.title')}
            </h1>
            <p className="text-gray-400 mt-2 leading-relaxed">{t('scans.scanHistory')}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              className={`p-3 bg-brand-black-light border border-gray-700 rounded-lg hover:border-brand-green/50 transition-all ${isRefreshing ? 'animate-spin' : ''}`}
              title="Rafraîchir"
            >
              <RefreshCw className="w-5 h-5 text-gray-400" />
            </button>
            <PlanRestrictedButton
              feature="scan_basic"
              quotaType="scans_per_month"
              onClick={() => openScanWizard(setIsAIWizardOpen)}
              className="bg-gradient-to-r from-brand-green to-green-600 text-brand-black px-6 py-3 rounded-lg font-bold hover:from-green-600 hover:to-brand-green transition-all inline-flex items-center gap-2 shadow-lg"
              disabledClassName="!bg-gray-700 !from-gray-700 !to-gray-700 !text-gray-400"
              showLockIcon={true}
              tooltipPosition="bottom"
            >
              <Brain className="w-5 h-5" />
              <span>{t('scans.types.ai_scan')}</span>
            </PlanRestrictedButton>
            <button
              onClick={() => alert('Scan Expert : Fonctionnalité en cours de développement')}
              className="bg-gray-700 text-gray-400 px-6 py-3 rounded-lg font-bold inline-flex items-center gap-2 shadow-lg cursor-not-allowed opacity-70"
            >
              <Shield className="w-5 h-5" />
              <span>{t('scans.types.expert')}</span>
            </button>
            <button
              onClick={() => alert('Scan API : Fonctionnalité en cours de développement')}
              className="bg-gray-700 text-gray-400 px-6 py-3 rounded-lg font-bold inline-flex items-center gap-2 shadow-lg cursor-not-allowed opacity-70"
            >
              <Globe className="w-5 h-5" />
              <span>{t('scans.types.api_pentest')}</span>
            </button>
            <button
              onClick={() => alert('Scan OT/ICS : Fonctionnalité en cours de développement')}
              className="bg-gray-700 text-gray-400 px-6 py-3 rounded-lg font-bold inline-flex items-center gap-2 shadow-lg cursor-not-allowed opacity-70"
            >
              <Factory className="w-5 h-5" />
              <span>{t('scans.types.ot_scan')}</span>
            </button>
            <button
              onClick={() => alert('Scan IoT : Fonctionnalité en cours de développement')}
              className="bg-gray-700 text-gray-400 px-6 py-3 rounded-lg font-bold inline-flex items-center gap-2 shadow-lg cursor-not-allowed opacity-70"
            >
              <Wifi className="w-5 h-5" />
              <span>{t('scans.types.iot_scan')}</span>
            </button>
            <button
              onClick={() => alert('Scan Cloud : Fonctionnalité en cours de développement')}
              className="bg-gray-700 text-gray-400 px-6 py-3 rounded-lg font-bold inline-flex items-center gap-2 shadow-lg cursor-not-allowed opacity-70"
            >
              <Cloud className="w-5 h-5" />
              <span>{t('scans.types.cloud_scan')}</span>
            </button>
          </div>
        </div>

        {/* Stats Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 overflow-visible">
          {/* Total Scans */}
          <div className="bg-gradient-to-br from-brand-black-light to-brand-black border border-brand-green/30 rounded-xl p-4 hover:border-brand-green/50 transition-all relative group cursor-help overflow-hidden">
            <div className="flex items-center justify-between mb-2 min-w-0">
              <span className="text-gray-400 text-sm truncate mr-2">{t('scans.title')}</span>
              <Target className="w-5 h-5 text-brand-green flex-shrink-0" />
            </div>
            <div className="text-3xl font-bold text-white">{stats.total}</div>
            <div className="text-xs text-gray-400 mt-1">{t('scans.allScans')}</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">{t('scans.tooltips.totalScans')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('scans.tooltips.totalScansDesc')}</p>
            </div>
          </div>

          {/* Running */}
          <div className="bg-gradient-to-br from-blue-900/30 to-brand-black border border-blue-500/30 rounded-xl p-4 hover:border-blue-500/50 transition-all relative group cursor-help overflow-hidden">
            <div className="flex items-center justify-between mb-2 min-w-0">
              <span className="text-gray-400 text-sm truncate mr-2">{t('scans.status.running')}</span>
              <Loader className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0" />
            </div>
            <div className="text-3xl font-bold text-blue-400">{stats.running}</div>
            <div className="text-xs text-blue-400 mt-1">{t('scans.activeScans')}</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-blue-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Loader className="w-4 h-4 text-blue-400" />
                <span className="font-semibold text-white">{t('scans.tooltips.runningScans')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('scans.tooltips.runningScansDesc')}</p>
            </div>
          </div>

          {/* Completed */}
          <div className="bg-gradient-to-br from-green-900/30 to-brand-black border border-brand-green/30 rounded-xl p-4 hover:border-brand-green/50 transition-all relative group cursor-help overflow-hidden">
            <div className="flex items-center justify-between mb-2 min-w-0">
              <span className="text-gray-400 text-sm truncate mr-2">{t('scans.status.completed')}</span>
              <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0" />
            </div>
            <div className="text-3xl font-bold text-brand-green">{stats.completed}</div>
            <div className="text-xs text-brand-green mt-1">{t('scans.withSuccess')}</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">{t('scans.tooltips.completedScans')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('scans.tooltips.completedScansDesc')}</p>
            </div>
          </div>

          {/* Total Vulnerabilities */}
          <div className="bg-gradient-to-br from-red-900/30 to-brand-black border border-red-500/30 rounded-xl p-4 hover:border-red-500/50 transition-all relative group cursor-help overflow-hidden">
            <div className="flex items-center justify-between mb-2 min-w-0">
              <span className="text-gray-400 text-sm truncate mr-2">{t('scans.vulnerabilities')}</span>
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            </div>
            <div className="text-3xl font-bold text-red-400">{stats.totalVulns}</div>
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              <span className="text-xs px-1.5 py-0.5 bg-purple-500/30 text-purple-400 rounded">{stats.criticalVulns}&nbsp;crit.</span>
              <span className="text-xs px-1.5 py-0.5 bg-red-500/30 text-red-400 rounded">{stats.highVulns}&nbsp;élev.</span>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-brand-black border border-red-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">{t('scans.tooltips.vulnerabilities')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('scans.tooltips.vulnerabilitiesDesc')}</p>
            </div>
          </div>

          {/* Hosts Discovered */}
          <div className="bg-gradient-to-br from-purple-900/30 to-brand-black border border-purple-500/30 rounded-xl p-4 hover:border-purple-500/50 transition-all relative group cursor-help overflow-hidden">
            <div className="flex items-center justify-between mb-2 min-w-0">
              <span className="text-gray-400 text-sm truncate mr-2">{t('scans.hosts')}</span>
              <Server className="w-5 h-5 text-purple-400 flex-shrink-0" />
            </div>
            <div className="text-3xl font-bold text-purple-400">{stats.totalHosts}</div>
            <div className="text-xs text-purple-400 mt-1">{t('scans.discovered')}</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-purple-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-4 h-4 text-purple-400" />
                <span className="font-semibold text-white">{t('scans.tooltips.hostsDiscovered')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('scans.tooltips.hostsDiscoveredDesc')}</p>
            </div>
          </div>

          {/* Failed */}
          <div className="bg-gradient-to-br from-orange-900/30 to-brand-black border border-orange-500/30 rounded-xl p-4 hover:border-orange-500/50 transition-all relative group cursor-help overflow-hidden">
            <div className="flex items-center justify-between mb-2 min-w-0">
              <span className="text-gray-400 text-sm truncate mr-2">{t('scans.status.failed')}</span>
              <XCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
            </div>
            <div className="text-3xl font-bold text-orange-400">{stats.failed}</div>
            <div className="text-xs text-orange-400 mt-1">{t('scans.toReview')}</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-orange-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-orange-400" />
                <span className="font-semibold text-white">{t('scans.tooltips.failedScans')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('scans.tooltips.failedScansDesc')}</p>
            </div>
          </div>
        </div>

        {/* No Workspace Modal */}
        <NoWorkspaceModal
          isOpen={showNoWorkspaceModal}
          onClose={() => setShowNoWorkspaceModal(false)}
        />

        {/* AI Wizard Modal */}
        <AIScanWizard
          isOpen={isAIWizardOpen}
          onClose={() => setIsAIWizardOpen(false)}
          onComplete={async (formData) => {
            const newScan = await scansApi.create(formData);
            navigate(`/scans/${newScan.id}`);
          }}
        />

        {/* Expert Wizard Modal */}
        <ExpertScanWizard
          isOpen={isExpertWizardOpen}
          onClose={() => setIsExpertWizardOpen(false)}
          onComplete={async (formData) => {
            const newScan = await scansApi.create(formData);
            navigate(`/scans/${newScan.id}`);
          }}
        />

        {/* API Wizard Modal */}
        <APIScanWizard
          isOpen={isAPIWizardOpen}
          onClose={() => setIsAPIWizardOpen(false)}
          onComplete={async (formData) => {
            const newScan = await scansApi.create({
              ...formData,
              target: formData.target_url,
              scan_type: 'api_pentest'
            });
            navigate(`/scans/${newScan.id}`);
          }}
        />

        {/* OT/ICS Wizard Modal */}
        <OTScanWizard
          isOpen={isOTWizardOpen}
          onClose={() => setIsOTWizardOpen(false)}
          onComplete={async (formData) => {
            const newScan = await otScanApi.create(formData);
            navigate(`/scans/${newScan.id}`);
          }}
        />

        {/* IoT Wizard Modal */}
        <IoTScanWizard
          isOpen={isIoTWizardOpen}
          onClose={() => setIsIoTWizardOpen(false)}
          onComplete={(scanResult) => {
            // Navigate to the IoT scan details page
            navigate(`/scans/iot/${scanResult.id}`);
          }}
        />

        {/* Toolbar */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder={t('scans.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                  filter === 'all'
                    ? 'bg-brand-green text-brand-black'
                    : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-brand-green/50'
                }`}
              >
                {t('scans.filters.all')} ({stats.total})
              </button>
              <button
                onClick={() => setFilter('running')}
                className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                  filter === 'running'
                    ? 'bg-blue-500 text-white'
                    : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-blue-500/50'
                }`}
              >
                {t('scans.status.running')} ({stats.running})
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                  filter === 'completed'
                    ? 'bg-brand-green text-brand-black'
                    : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-brand-green/50'
                }`}
              >
                {t('scans.status.completed')} ({stats.completed})
              </button>
              <button
                onClick={() => setFilter('failed')}
                className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                  filter === 'failed'
                    ? 'bg-red-500 text-white'
                    : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-red-500/50'
                }`}
              >
                {t('scans.filters.failed')} ({stats.failed})
              </button>
            </div>

            {/* Sort & View */}
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-gray-300 text-sm focus:outline-none focus:border-brand-green/50"
              >
                <option value="date">{t('scans.sortByDate')}</option>
                <option value="status">{t('scans.sortByStatus')}</option>
                <option value="vulns">{t('scans.sortByVulns')}</option>
              </select>
              <div className="flex items-center border border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-brand-green text-brand-black' : 'bg-brand-black text-gray-400 hover:text-white'}`}
                >
                  <List className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-brand-green text-brand-black' : 'bg-brand-black text-gray-400 hover:text-white'}`}
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scans List/Grid */}
        {viewMode === 'list' ? (
          // List View - HTML Table
          <div className="bg-brand-black-light rounded-xl border border-brand-green/20 overflow-x-auto">
            <table className="w-full table-auto min-w-[1200px]">
              <thead>
                <tr className="bg-brand-black border-b border-gray-800 text-sm font-semibold text-gray-400">
                  <th className="px-3 py-4 text-left whitespace-nowrap">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedScans(paginatedScans.map(s => s.id));
                        } else {
                          setSelectedScans([]);
                        }
                      }}
                      checked={selectedScans.length === paginatedScans.length && paginatedScans.length > 0}
                      className="w-4 h-4 accent-brand-green"
                    />
                  </th>
                  <th
                    className="px-3 py-4 text-left cursor-pointer hover:text-brand-green transition-colors select-none whitespace-nowrap min-w-[80px]"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      <Tag className="w-3 h-3 flex-shrink-0" />
                      {t('scans.table.name')}
                      {sortBy === 'name' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                      {sortBy !== 'name' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-4 text-center cursor-pointer hover:text-brand-green transition-colors select-none relative group/score whitespace-nowrap min-w-[90px]"
                    onClick={() => handleSort('score')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <Gauge className="w-3 h-3 flex-shrink-0" />
                      {t('scans.table.score')}
                      {sortBy === 'score' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                      {sortBy !== 'score' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                    </div>
                    {/* Tooltip légende CyberScore */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 opacity-0 invisible group-hover/score:opacity-100 group-hover/score:visible transition-all duration-200 pointer-events-none">
                      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 text-left min-w-[180px]">
                        <div className="text-xs font-bold text-white mb-2 border-b border-gray-700 pb-1">Légende CyberScore</div>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-green-400 font-medium">90-100</span>
                            <span className="text-green-400">Excellent</span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-brand-green font-medium">70-89</span>
                            <span className="text-brand-green">Bon</span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-yellow-400 font-medium">50-69</span>
                            <span className="text-yellow-400">Moyen</span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-orange-400 font-medium">30-49</span>
                            <span className="text-orange-400">Faible</span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-red-400 font-medium">0-29</span>
                            <span className="text-red-400">Critique</span>
                          </div>
                        </div>
                        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-900 border-l border-t border-gray-700 rotate-45"></div>
                      </div>
                    </div>
                  </th>
                  <th
                    className="px-3 py-4 text-left cursor-pointer hover:text-brand-green transition-colors select-none whitespace-nowrap min-w-[80px]"
                    onClick={() => handleSort('target')}
                  >
                    <div className="flex items-center gap-1">
                      {t('scans.table.target')}
                      {sortBy === 'target' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                      {sortBy !== 'target' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                    </div>
                  </th>
                  <th className="px-3 py-4 text-left whitespace-nowrap min-w-[80px]">{t('scans.table.client')}</th>
                  <th
                    className="px-3 py-4 text-left cursor-pointer hover:text-brand-green transition-colors select-none whitespace-nowrap min-w-[70px]"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center gap-1">
                      {t('scans.table.type')}
                      {sortBy === 'type' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                      {sortBy !== 'type' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-4 text-center cursor-pointer hover:text-brand-green transition-colors select-none whitespace-nowrap min-w-[90px]"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {t('scans.table.status')}
                      {sortBy === 'status' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                      {sortBy !== 'status' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-4 text-center cursor-pointer hover:text-brand-green transition-colors select-none whitespace-nowrap min-w-[80px]"
                    onClick={() => handleSort('vulns')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {t('scans.table.vulnerabilities')}
                      {sortBy === 'vulns' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                      {sortBy !== 'vulns' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                    </div>
                  </th>
                  <th
                    className="px-3 py-4 text-left cursor-pointer hover:text-brand-green transition-colors select-none whitespace-nowrap min-w-[80px]"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center gap-1">
                      {t('scans.table.date')}
                      {sortBy === 'date' && (
                        sortDirection === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
                      )}
                      {sortBy !== 'date' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                    </div>
                  </th>
                  <th className="px-3 py-4 text-center whitespace-nowrap min-w-[90px]">{t('scans.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedScans.length > 0 ? (
                  paginatedScans.map((scan) => (
                    <tr
                      key={scan.id}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, scan.id)}
                      onDragEnd={handleDragEnd}
                      className={`border-b border-gray-800/50 hover:bg-brand-green/5 transition-all cursor-pointer ${
                        selectedScans.includes(scan.id) ? 'bg-brand-green/10' : ''
                      } ${draggedScanId === scan.id ? 'opacity-50 bg-brand-green/20' : ''}`}
                      onClick={() => navigate(`/scans/${scan.id}`)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-3 h-3 text-gray-500 cursor-grab hover:text-brand-green transition-colors flex-shrink-0" />
                          <input
                            type="checkbox"
                            checked={selectedScans.includes(scan.id)}
                            onChange={() => toggleSelectScan(scan.id)}
                            className="w-4 h-4 accent-brand-green flex-shrink-0"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {scan.name ? (
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Tag className="w-3 h-3 text-brand-green flex-shrink-0" />
                            <span className="text-gray-200 text-sm truncate" title={scan.name}>
                              {scan.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs italic">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {(() => {
                          const cyberScore = getCyberScore(scan);
                          return (
                            <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${cyberScore.bgColor} ${cyberScore.borderColor}`}>
                              {cyberScore.score !== null ? (
                                <>
                                  <span className={`font-bold text-sm ${cyberScore.color}`}>{cyberScore.score}</span>
                                  <span className={`text-xs ${cyberScore.color} opacity-75`}>{cyberScore.label}</span>
                                </>
                              ) : (
                                <>
                                  <Loader className="w-3 h-3 text-blue-400 animate-spin" />
                                  <span className="text-xs text-blue-400">{cyberScore.label}</span>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3" style={{ minWidth: '180px', maxWidth: '280px' }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-semibold text-white hover:text-brand-green transition-colors text-sm font-mono truncate" style={{ maxWidth: '240px' }} title={scan.target}>
                            {scan.target}
                          </span>
                          {(scan.total_hosts_found || scan.hosts_up) > 0 && (
                            <span className="text-xs text-gray-500 flex-shrink-0">({scan.total_hosts_found || scan.hosts_up})</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {scan.workspaces && scan.workspaces.length > 0 ? (
                          <div className="flex items-center gap-1 min-w-0">
                            <Building2 className="w-3 h-3 text-brand-green flex-shrink-0" />
                            <span className="text-gray-300 text-xs truncate" title={scan.workspaces.map(w => w.company_name || w.name).join(', ')}>
                              {scan.workspaces[0].company_name || scan.workspaces[0].name}
                              {scan.workspaces.length > 1 && (
                                <span className="text-gray-500 ml-1">+{scan.workspaces.length - 1}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs italic">Non assigné</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-gray-300 text-xs">{translateScanType(scan.scan_type)}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {getStatusIcon(scan.status)}
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(scan.status)} ${scan.status === 'failed' && scan.error_message ? 'cursor-help' : ''}`}
                            title={scan.status === 'failed' && scan.error_message ? scan.error_message : undefined}
                          >
                            {t(`scans.status.${scan.status}`, scan.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="grid grid-cols-2 gap-1 min-w-[120px]">
                          {scan.total_vulnerabilities > 0 ? (
                            <>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-bold text-center truncate ${scan.critical_count > 0 ? 'bg-purple-500/30 text-purple-400' : 'bg-gray-700 text-gray-500'}`}>
                                {scan.critical_count || 0}&nbsp;crit.
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-bold text-center truncate ${scan.high_count > 0 ? 'bg-red-500/30 text-red-400' : 'bg-gray-700 text-gray-500'}`}>
                                {scan.high_count || 0}&nbsp;élev.
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-bold text-center truncate ${scan.medium_count > 0 ? 'bg-orange-500/30 text-orange-400' : 'bg-gray-700 text-gray-500'}`}>
                                {scan.medium_count || 0}&nbsp;moy.
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-bold text-center truncate ${scan.low_count > 0 ? 'bg-yellow-500/30 text-yellow-400' : 'bg-gray-700 text-gray-500'}`}>
                                {scan.low_count || 0}&nbsp;bas
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-500 text-xs col-span-2 text-center">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 min-w-[100px]">
                        <span className="text-xs text-gray-400 whitespace-nowrap" title={new Date(scan.created_at).toLocaleString('fr-FR')}>
                          {new Date(scan.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      </td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => navigate(`/scans/${scan.id}`)}
                            className="p-1.5 hover:bg-brand-green/20 rounded transition-colors"
                            title="Voir détails"
                          >
                            <Eye className="w-3.5 h-3.5 text-gray-400 hover:text-brand-green" />
                          </button>
                          {(scan.status === 'running' || scan.status === 'pending') && (
                            <button
                              onClick={(e) => handleStopScan(e, scan.id)}
                              className="p-1.5 hover:bg-orange-500/20 rounded transition-colors"
                              title="Arrêter le scan"
                            >
                              <Pause className="w-3.5 h-3.5 text-gray-400 hover:text-orange-400" />
                            </button>
                          )}
                          {scan.status === 'stopped' && (
                            <button
                              onClick={(e) => handleResumeScan(e, scan.id)}
                              className="p-1.5 hover:bg-green-500/20 rounded transition-colors"
                              title="Reprendre le scan"
                            >
                              <RotateCcw className="w-3.5 h-3.5 text-gray-400 hover:text-green-400" />
                            </button>
                          )}
                          {/* PDF Export Button */}
                          <button
                            onClick={(e) => handleExport(e, scan.id, 'pdf')}
                            className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                            title="Télécharger en PDF"
                            disabled={isExporting || scan.status !== 'completed'}
                          >
                            <FileText className={`w-3.5 h-3.5 ${scan.status === 'completed' ? 'text-red-400 hover:text-red-300' : 'text-gray-600'}`} />
                          </button>
                          {/* Word Export Button */}
                          <button
                            onClick={(e) => handleExport(e, scan.id, 'docx')}
                            className="p-1.5 hover:bg-blue-500/20 rounded transition-colors"
                            title="Télécharger en Word"
                            disabled={isExporting || scan.status !== 'completed'}
                          >
                            <Download className={`w-3.5 h-3.5 ${scan.status === 'completed' ? 'text-blue-400 hover:text-blue-300' : 'text-gray-600'}`} />
                          </button>
                          {/* Regenerate Reports Button */}
                          <button
                            onClick={(e) => handleRegenerateReports(e, scan.id)}
                            className="p-1.5 hover:bg-purple-500/20 rounded transition-colors ml-1"
                            title="Régénérer les rapports PDF et Word"
                            disabled={regeneratingScanId === scan.id || scan.status !== 'completed'}
                          >
                            {regeneratingScanId === scan.id ? (
                              <Loader className="w-3.5 h-3.5 text-purple-400 animate-spin" />
                            ) : (
                              <RotateCcw className={`w-3.5 h-3.5 ${scan.status === 'completed' ? 'text-purple-400 hover:text-purple-300' : 'text-gray-600'}`} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="p-12 text-center">
                      <Target className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-gray-300 mb-2">Aucun scan trouvé</h3>
                      <p className="text-gray-500 mb-6">
                        {searchQuery
                          ? `Aucun résultat pour "${searchQuery}"`
                          : filter === 'all'
                            ? 'Commencez votre premier scan de sécurité'
                            : `Aucun scan avec le statut "${filter}"`
                        }
                      </p>
                      <LicenseBlock feature="les scans">
                      <PlanRestrictedButton
                        feature="scan_basic"
                        quotaType="scans_per_month"
                        onClick={() => setIsAIWizardOpen(true)}
                        className="btn-green inline-flex items-center gap-2"
                        disabledClassName="!bg-gray-700 !text-gray-400"
                        showLockIcon={true}
                      >
                        <Plus className="w-5 h-5" />
                        <span>Nouveau scan</span>
                      </PlanRestrictedButton>
                      </LicenseBlock>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          // Grid View
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginatedScans.length > 0 ? (
              paginatedScans.map((scan) => (
                <div
                  key={scan.id}
                  onClick={() => navigate(`/scans/${scan.id}`)}
                  className="bg-brand-black-light rounded-xl p-5 border border-brand-green/20 hover:border-brand-green/50 hover:shadow-glow-green transition-all duration-300 group cursor-pointer"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(scan.status)}
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(scan.status)} ${scan.status === 'failed' && scan.error_message ? 'cursor-help' : ''}`}
                        title={scan.status === 'failed' && scan.error_message ? scan.error_message : undefined}
                      >
                        {t(`scans.status.${scan.status}`, scan.status)}
                      </span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); }}
                      className="p-1 hover:bg-gray-800 rounded"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>

                  {/* Target */}
                  <h3 className="text-lg font-bold text-white group-hover:text-brand-green transition-colors mb-2 font-mono" style={{ wordBreak: 'break-word' }} title={scan.target}>
                    {scan.target}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">{translateScanType(scan.scan_type)}</p>

                  {/* Vulns Summary */}
                  <div className="bg-brand-black rounded-lg p-3 mb-4">
                    <div className="text-xs text-gray-400 mb-2">Vulnérabilités</div>
                    <div className="flex items-center gap-2">
                      {scan.total_vulnerabilities > 0 ? (
                        <>
                          <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden flex">
                            {scan.critical_count > 0 && (
                              <div
                                className="h-full bg-purple-500"
                                style={{ width: `${(scan.critical_count / scan.total_vulnerabilities) * 100}%` }}
                              />
                            )}
                            {scan.high_count > 0 && (
                              <div
                                className="h-full bg-red-500"
                                style={{ width: `${(scan.high_count / scan.total_vulnerabilities) * 100}%` }}
                              />
                            )}
                            {scan.medium_count > 0 && (
                              <div
                                className="h-full bg-orange-500"
                                style={{ width: `${(scan.medium_count / scan.total_vulnerabilities) * 100}%` }}
                              />
                            )}
                            {scan.low_count > 0 && (
                              <div
                                className="h-full bg-yellow-500"
                                style={{ width: `${(scan.low_count / scan.total_vulnerabilities) * 100}%` }}
                              />
                            )}
                          </div>
                          <span className="text-white font-bold text-sm">{scan.total_vulnerabilities}</span>
                        </>
                      ) : (
                        <span className="text-gray-400 text-sm">Aucune vulnérabilité</span>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-1" title={new Date(scan.created_at).toLocaleString('fr-FR')}>
                      <Calendar className="w-3 h-3" />
                      {new Date(scan.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                    {(scan.total_hosts_found || scan.hosts_up) > 0 && (
                      <div className="flex items-center gap-1">
                        <Server className="w-3 h-3" />
                        {scan.total_hosts_found || scan.hosts_up} hôtes
                      </div>
                    )}
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-brand-green group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full bg-brand-black-light rounded-xl p-12 border border-brand-green/20 text-center">
                <Target className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-300 mb-2">Aucun scan trouvé</h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery
                    ? `Aucun résultat pour "${searchQuery}"`
                    : 'Commencez votre premier scan de sécurité'
                  }
                </p>
                <LicenseBlock feature="les scans">
                <PlanRestrictedButton
                  feature="scan_basic"
                  quotaType="scans_per_month"
                  onClick={() => setIsAIWizardOpen(true)}
                  className="btn-green inline-flex items-center gap-2"
                  disabledClassName="!bg-gray-700 !text-gray-400"
                  showLockIcon={true}
                >
                  <Plus className="w-5 h-5" />
                  <span>Nouveau scan</span>
                </PlanRestrictedButton>
                </LicenseBlock>
              </div>
            )}
          </div>
        )}

        {/* Pagination Controls */}
        {filteredScans.length > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
            <div className="text-sm text-gray-400">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredScans.length)} sur {filteredScans.length} scans
            </div>
            <div className="flex items-center gap-2">
              {/* First Page */}
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg transition-all ${
                  currentPage === 1
                    ? 'text-gray-600 cursor-not-allowed'
                    : 'text-gray-400 hover:text-brand-green hover:bg-brand-green/10'
                }`}
                title="Première page"
              >
                <ChevronsLeft className="w-5 h-5" />
              </button>

              {/* Previous Page */}
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg transition-all ${
                  currentPage === 1
                    ? 'text-gray-600 cursor-not-allowed'
                    : 'text-gray-400 hover:text-brand-green hover:bg-brand-green/10'
                }`}
                title="Page précédente"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and neighbors
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, idx, arr) => {
                    // Add ellipsis if there's a gap
                    const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <span className="px-2 text-gray-500">...</span>
                        )}
                        <button
                          onClick={() => goToPage(page)}
                          className={`min-w-[36px] h-9 px-3 rounded-lg font-medium transition-all ${
                            currentPage === page
                              ? 'bg-brand-green text-brand-black'
                              : 'text-gray-400 hover:text-brand-green hover:bg-brand-green/10'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              {/* Next Page */}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg transition-all ${
                  currentPage === totalPages
                    ? 'text-gray-600 cursor-not-allowed'
                    : 'text-gray-400 hover:text-brand-green hover:bg-brand-green/10'
                }`}
                title="Page suivante"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {/* Last Page */}
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg transition-all ${
                  currentPage === totalPages
                    ? 'text-gray-600 cursor-not-allowed'
                    : 'text-gray-400 hover:text-brand-green hover:bg-brand-green/10'
                }`}
                title="Dernière page"
              >
                <ChevronsRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Validation Message */}
        {validationMessage && (
          <div className={`fixed top-6 right-6 px-6 py-4 rounded-xl shadow-2xl z-50 flex items-center gap-3 ${
            validationMessage.type === 'success'
              ? 'bg-brand-green/20 border border-brand-green text-brand-green'
              : 'bg-red-500/20 border border-red-500 text-red-400'
          }`}>
            {validationMessage.type === 'success' ? (
              <BadgeCheck className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{validationMessage.text}</span>
          </div>
        )}

        {/* Association Message Toast */}
        {associationMessage && (
          <div className={`fixed top-6 right-6 px-6 py-4 rounded-xl shadow-2xl z-50 flex items-center gap-3 animate-pulse ${
            associationMessage.type === 'success'
              ? 'bg-brand-green/20 border border-brand-green text-brand-green'
              : 'bg-red-500/20 border border-red-500 text-red-400'
          }`}>
            {associationMessage.type === 'success' ? (
              <Link2 className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{associationMessage.text}</span>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedScans.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-brand-black-light border border-brand-green/50 rounded-xl px-6 py-4 shadow-2xl flex items-center gap-6 z-50">
            <span className="text-white font-medium">{selectedScans.length} scan(s) sélectionné(s)</span>
            <div className="h-6 w-px bg-gray-700" />
            <button
              onClick={handleValidateForReport}
              disabled={isValidating}
              className="flex items-center gap-2 bg-brand-green text-brand-black px-4 py-2 rounded-lg font-bold hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {isValidating ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <BadgeCheck className="w-4 h-4" />
              )}
              <span>Valider pour rapport</span>
            </button>
            <button className="flex items-center gap-2 text-gray-300 hover:text-brand-green transition-colors">
              <FileText className="w-4 h-4" />
              <span>Rapport</span>
            </button>
            <button className="flex items-center gap-2 text-brand-green hover:text-green-400 transition-colors">
              <Download className="w-4 h-4" />
              <span>Exporter</span>
            </button>
            <button
              onClick={handleRescan}
              disabled={isRescanning}
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
            >
              {isRescanning ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              <span>Relancer</span>
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 text-gray-300 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span>Supprimer</span>
            </button>
            <button
              onClick={() => setSelectedScans([])}
              className="text-gray-500 hover:text-white transition-colors"
            >
              <span>Annuler</span>
            </button>
          </div>
        )}

        {/* Sankey Workflow Section - Bottom */}
        <div className="mt-8">
          <SankeyWorkspaceFlow scans={scansList} workspaces={workspacesList} />
        </div>
        </div>
    </div>
  );
};

export default ScansList;
